import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { stripeConnectApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../shared/logger';
import type { SubscriptionPlan } from '../types/subscription';
import { useFacility } from '../hooks/useFacility';

const PlanSelection: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const facilityId = searchParams.get('facilityId');
  const businessId = searchParams.get('businessId') || localStorage.getItem('businessId');
  const { facility, loading: facilityLoading, error: facilityError } = useFacility(facilityId || undefined, businessId || undefined);

  useEffect(() => {
    if (!currentUser) {
      navigate('/member/login');
      return;
    }

    if (!facilityId || !businessId) {
      logger.error('Missing facility ID or business ID');
      navigate('/member/register');
      return;
    }

    if (facility && facility.isPPU) {
      navigate(`/member/register?facilityId=${facilityId}&businessId=${businessId}&ppu=1`);
      return;
    }

    // Check for invite code in session storage
    const inviteCodeId = sessionStorage.getItem('invite_code_id');
    if (!inviteCodeId || inviteCodeId === 'null') {
      logger.error('No invite code ID found in session storage');
      navigate('/member/register');
      return;
    }

    const fetchPlans = async () => {
      try {
        const { plans: facilityPlans } = await stripeConnectApi.getPlans(businessId, facilityId);
        setPlans(facilityPlans);
      } catch (err: any) {
        logger.error('Error fetching plans:', err);
        setError('Failed to load subscription plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [currentUser, facilityId, businessId, facility, navigate]);

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
  };

  const handleContinue = () => {
    if (selectedPlan) {
      navigate(`/member/new-subscription?facilityId=${facilityId}&businessId=${businessId}&planId=${selectedPlan.priceId}`);
    }
  };

  if (facilityLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (facilityError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 p-4 rounded-md">
          <p className="text-red-600">{facilityError}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 p-4 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Choose Your Plan
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Select a subscription plan that works best for you
          </p>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all ${
                selectedPlan?.id === plan.id ? 'ring-2 ring-blue-600' : 'hover:shadow-lg'
              }`}
              onClick={() => handlePlanSelect(plan)}
            >
              <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
              <div className="mt-4">
                <p className="text-3xl font-bold text-gray-900">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: plan.currency || 'USD',
                  }).format(plan.amount / 100)}
                </p>
                <p className="text-gray-500">per {plan.interval}</p>
              </div>
            </div>
          ))}
        </div>

        {selectedPlan && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleContinue}
              className="bg-blue-600 text-white px-8 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Continue with {selectedPlan.name}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export { PlanSelection };
export default PlanSelection; 